import { useEffect, useRef, useState } from "react";

import { useDentalStore } from "../../Store/useDentalReport";
import { fetchReportData } from "../../services/network";
import Phi from "../../Phi/Phi";

import { teethGum, oralHygiene } from "../../Assets/images";

import StudentCard from "../../Components/UI/StudentCard";
import ComplaintsCard from "../../Components/UI/ComplaintsCard";
import MultilineTextBox from "../../Components/UI/MultilineTextBox";
import Precaution from "../../Components/UI/Precaution";
import ReportFooter from "../../Components/UI/ReportFooter";
import SectionComplaintsCard from "../../Components/UI/SectionComplaintCard";
import SuggestedInvestigationCard from "../../Components/UI/SuggestedInvestigationCard";

import ExaminationOfGingiva from "../../Components/report/dentalQuad/ExaminationOfGingiva";
import ExaminationOfTeeth from "../../Components/report/dentalQuad/ExaminationOfTeeth";

import ScreenShotWrapper from "../../Components/report/ScreenShotWrapper";

import SimpleLoader from "../../Components/Loaders/SimpleLoader";

import {
  DamageTeethSvgicon,
  EditSvg,
  GirlIconSvg,
  MalocclusionIconSvg,
  OralHealthIconSvg,
  SearchSuggestionIconSvg,
  TeethIconSvg,
  UserIconSvg,
} from "../../utils/Svgs";

import {
  ReportRXData,
  reportStrings,
} from "../../utils/helpers/newReportHelper";

import { PALLETTE } from "../../utils/theme";
import { V2ReportStyle } from "../../styles/V2ReportStyle";
import Page from "./Page";
import { useUserStore } from "../../Store/userStore";
import { generateReportLogic } from "../../services/reportService";
import ReportHeader from "../../Components/report/ReportHeader";
import AssessmentNotDoneWrapper from "../../Components/report/AssessmentNotDoneWrapper";

const DentalReportScreen = ({ location }: { location: any }) => {
  const dentalReport = useDentalStore();
  const { handleMspLogo, mspLogo } = useUserStore((state) => state);
  const sessionData = sessionStorage.getItem("report_navigation_state");
  const storedParams = sessionData ? JSON.parse(sessionData) : {};
  const [assessmentError, setAssessmentError] = useState(null);
  // Reads location.state if navigated internally, falls back to sessionStorage for new tab
  const params = location?.state || storedParams;

  const [isLoading, setLoading] = useState(false);

  const pdfWrapperRef = useRef<any>(null);

  const studentId = params?.user_id || params?.student_id;
  const eventTrackId = params?.eventTrackId;
  const activityId = params?.activityId;

  const jwt = Phi.getSession()?.jwt;

  const getchData = async (activity_id: number, assessmentId: number) => {
    try {
      setLoading(true);

      const res = await fetchReportData({
        activity_id,
        student_id: studentId,
        jwt,
        event_track_id: assessmentId,
      });

      if (res?.results?.length > 0) {
        const parsedAssessment = JSON.parse(res.results[0].assessmentdata);
        if (parsedAssessment?.data?.Eye_Incomplete_Remark == "105") {
          setAssessmentError(true);
        }

        dentalReport.setDentalReportData({
          data: parsedAssessment,
        });
        await generateReportLogic({
          jwt: jwt,
          studentId: studentId,
          handleMspLogo,
        });
      }
    } catch (error) {
      console.error("error getting dental report", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getchData(activityId, eventTrackId);
  }, []);

  return (
    <div
      style={{
        ...V2ReportStyle.mainContainer,
        backgroundColor: PALLETTE.white,
        minHeight: "100vh",
      }}
    >
      {isLoading ? (
        <SimpleLoader />
      ) : (
        <>
          <ReportHeader title="Dental Report" pdfWrapperRef={pdfWrapperRef} />

          <ScreenShotWrapper
            ref={pdfWrapperRef}
            studentName={params?.name || params?.studentname}
            reportType="Dental"
          >
            {(targetRef: any) => (
              <div
                ref={targetRef}
                style={{
                  padding: "3rem",
                  backgroundColor: PALLETTE.background,
                }}
              >
                {assessmentError ? (
                  <AssessmentNotDoneWrapper>
                    <StudentCard
                      name={params?.name || params?.studentname}
                      schoolName={params?.class}
                      icon={
                        params?.gender === "FEMALE" || params?.gender == "F" ? (
                          <GirlIconSvg />
                        ) : (
                          <UserIconSvg />
                        )
                      }
                    />
                  </AssessmentNotDoneWrapper>
                ) : (
                  <Page>
                    <StudentCard
                      name={params?.name || params?.studentname}
                      schoolName={params?.class}
                      icon={
                        params?.gender === "FEMALE" || params?.gender == "F" ? (
                          <GirlIconSvg />
                        ) : (
                          <UserIconSvg />
                        )
                      }
                    />

                    <ComplaintsCard
                      label={
                        dentalReport.NeedAttention?.chiefComplaints
                          ? "Need Attention"
                          : "Normal"
                      }
                      remark={
                        dentalReport.DentalsOtherData?.Chief_Complaint_Others
                      }
                      data={dentalReport.ChiefComplaints}
                      topLefticon={<TeethIconSvg />}
                      title="Chief Complaint"
                      imageSource={teethGum}
                      suggestionText={
                        dentalReport.NeedAttention?.chiefComplaints
                          ? reportStrings.dental
                          : ""
                      }
                    />

                    <SectionComplaintsCard
                      topLefticon={<OralHealthIconSvg />}
                      title="Oral Hygiene"
                      imageSource={oralHygiene}
                      sections={["Habits_"]}
                      data={dentalReport.OralHygiene}
                      label={
                        dentalReport.NeedAttention?.oralHygiene
                          ? "Need Attention"
                          : "Normal"
                      }
                      remark={dentalReport.DentalsOtherData?.Habits_Others}
                      suggestionText={
                        dentalReport.NeedAttention?.chiefComplaints
                          ? reportStrings.dental
                          : ""
                      }
                    />

                    <ExaminationOfTeeth
                      fullChartData={dentalReport.DmftIndex}
                      label={
                        dentalReport.NeedAttention?.dmftIndex
                          ? "Need Attention"
                          : "Normal"
                      }
                      suggestionText={
                        dentalReport.NeedAttention?.chiefComplaints
                          ? reportStrings.dental
                          : ""
                      }
                    />

                    <ExaminationOfGingiva
                      label={
                        dentalReport.NeedAttention?.malocclusion
                          ? "Need Attention"
                          : "Normal"
                      }
                      data={dentalReport.MalocclusionData}
                      showHeading={false}
                      topLefticon={<MalocclusionIconSvg />}
                      title="Malocclusion"
                      suggestionText={
                        dentalReport.NeedAttention?.chiefComplaints
                          ? reportStrings.dental
                          : ""
                      }
                    />

                    <ExaminationOfGingiva
                      label={
                        dentalReport.NeedAttention?.stains
                          ? "Need Attention"
                          : "Normal"
                      }
                      data={dentalReport.StainsData}
                      topLefticon={<DamageTeethSvgicon />}
                      title="Stains"
                      suggestionText={
                        dentalReport.NeedAttention?.chiefComplaints
                          ? reportStrings.dental
                          : ""
                      }
                    />

                    <ExaminationOfGingiva
                      label={
                        dentalReport.NeedAttention?.calculus
                          ? "Need Attention"
                          : "Normal"
                      }
                      data={dentalReport.CalculusData}
                      showHeading={false}
                      topLefticon={<DamageTeethSvgicon />}
                      title="Calculus"
                      suggestionText={
                        dentalReport.NeedAttention?.chiefComplaints
                          ? reportStrings.dental
                          : ""
                      }
                    />

                    {dentalReport.DentalMetaData?.Dental_Remarks && (
                      <div style={V2ReportStyle.card}>
                        <MultilineTextBox
                          description={
                            dentalReport.DentalMetaData?.Dental_Remarks
                          }
                          label="Remarks"
                          icon={<EditSvg />}
                        />
                      </div>
                    )}

                    <SuggestedInvestigationCard
                      label={
                        dentalReport.NeedAttention?.suggestedInvestigation
                          ? "Need Attention"
                          : "Normal"
                      }
                      remark={dentalReport.DentalsOtherData?.Dental_Other_Tests}
                      data={dentalReport.SuggestedInvestigationData}
                      topLefticon={<SearchSuggestionIconSvg />}
                      title="Suggested Investigations"
                    />

                    <Precaution data={ReportRXData.dental} />

                    <ReportFooter
                      docSignature={
                        dentalReport.DentalMetaData?.Dental_Doctor_Path
                      }
                      designation={
                        dentalReport.DentalMetaData?.Dental_Doctor_Designation
                      }
                      doctorName={dentalReport.DentalMetaData?.Dental_Doctor_No}
                      hospitalName={mspLogo[eventTrackId]}
                    />
                  </Page>
                )}
              </div>
            )}
          </ScreenShotWrapper>
        </>
      )}
    </div>
  );
};

export default DentalReportScreen;
